import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { deleteVehicle, listVehicles, saveVehicle } from '@/lib/vehicles';
import type { VehicleDraft } from '@/types/Vehicle';

const VEHICLES_KEY = (uid: string) => ['vehicles', uid] as const;

export function useVehicles() {
  const { user } = useAuth();
  return useQuery({
    queryKey: VEHICLES_KEY(user?.uid ?? 'anon'),
    queryFn: () => (user ? listVehicles(user.uid) : Promise.resolve([])),
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function useSaveVehicle() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draft: VehicleDraft) => {
      if (!user) throw new Error('Not signed in');
      return saveVehicle(user.uid, draft);
    },
    onSuccess: () => {
      if (user) qc.invalidateQueries({ queryKey: VEHICLES_KEY(user.uid) });
    },
  });
}

export function useDeleteVehicle() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!user) throw new Error('Not signed in');
      return deleteVehicle(user.uid, id);
    },
    onSuccess: () => {
      if (user) qc.invalidateQueries({ queryKey: VEHICLES_KEY(user.uid) });
    },
  });
}
